import { supabase } from '../../../shared/supabase/supabaseClient';
import { addSentryBreadcrumb } from '../../../shared/sentry/sentryUser';
import { canRunOperationalBackgroundWork } from '../../session/accountSetupService';
import { SUPPORT_UNREAD_CACHE_MS } from '../../session/sessionCacheTtl';
import type { FeedbackAttachment } from '@shared/feedbackAttachments';

const SUPPORT_TICKETS_CACHE_MS = SUPPORT_UNREAD_CACHE_MS;
let supportTicketsInFlight: Promise<number> | null = null;
let supportTicketsCache: { value: number; fetchedAt: number } | null = null;

export type SupportTicketType = 'support' | 'improvement';
export type SupportTicketStatus = 'submitted' | 'triaged' | 'closed';
export type UserTicketResolution = 'ok' | 'not_resolved';

export type SupportThreadMessageRow = {
  id: string;
  author: 'staff' | 'commerce';
  body: string;
  staff_label: string | null;
  created_at: string;
  attachments?: FeedbackAttachment[];
};

export type SupportTicketRow = {
  id: string;
  organization_id: string;
  branch_id: string | null;
  auth_user_id: string;
  ticket_type: SupportTicketType;
  body: string;
  context: Record<string, unknown>;
  status: SupportTicketStatus;
  priority: string | null;
  created_at: string;
  public_reply: string | null;
  replied_at: string | null;
  replied_by: string | null;
  read_by_user_at: string | null;
  user_resolution: UserTicketResolution | null;
  user_resolution_at: string | null;
  user_resolution_note: string | null;
};

export type InsertSupportTicketInput = {
  organizationId: string;
  branchId: string | null;
  userId: string;
  ticketType: SupportTicketType;
  body: string;
  context: Record<string, unknown>;
};

const ticketSelect =
  'id, organization_id, branch_id, auth_user_id, ticket_type, body, context, status, priority, created_at, public_reply, replied_at, replied_by, read_by_user_at, user_resolution, user_resolution_at, user_resolution_note';

export async function insertSupportTicket(input: InsertSupportTicketInput): Promise<{ id: string }> {
  const body = input.body.trim();
  if (body.length < 1 || body.length > SUPPORT_TICKET_BODY_MAX) {
    throw new Error(
      body.length < 1
        ? 'Escribí un mensaje para enviar.'
        : `El mensaje no puede superar ${SUPPORT_TICKET_BODY_MAX} caracteres.`
    );
  }
  const { data, error } = await supabase
    .from('support_tickets')
    .insert({
      organization_id: input.organizationId,
      branch_id: input.branchId,
      auth_user_id: input.userId,
      ticket_type: input.ticketType,
      body,
      context: input.context,
      status: 'submitted',
    })
    .select('id')
    .single();

  if (error) {
    throw error;
  }
  if (!data?.id || typeof data.id !== 'string') {
    throw new Error('No se pudo registrar el ticket.');
  }
  addSentryBreadcrumb('support_ticket', `Ticket creado: ${input.ticketType}`, 'info', {
    ticketId: data.id,
    ticketType: input.ticketType,
    branchId: input.branchId,
  });
  return { id: data.id };
}

/** Tope de seguridad absoluto por página (evita pedidos desmedidos). */
export const SUPPORT_LIST_MY_TICKETS_LIMIT = 100;

export async function listMyTickets(
  userId: string,
  page?: { limit: number; offset: number }
): Promise<SupportTicketRow[]> {
  const limit = Math.min(page?.limit ?? SUPPORT_LIST_MY_TICKETS_LIMIT, SUPPORT_LIST_MY_TICKETS_LIMIT);
  const offset = page?.offset ?? 0;
  const { data, error } = await supabase
    .from('support_tickets')
    .select(ticketSelect)
    .eq('auth_user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw error;
  }
  return (data ?? []) as SupportTicketRow[];
}

/** Branch-scoped tickets for supervisors with Support.view_branch_tickets. */
export async function listBranchSupportTickets(
  branchId: string,
  page?: { limit: number; offset: number }
): Promise<SupportTicketRow[]> {
  const limit = Math.min(page?.limit ?? SUPPORT_LIST_MY_TICKETS_LIMIT, SUPPORT_LIST_MY_TICKETS_LIMIT);
  const offset = page?.offset ?? 0;
  const { data, error } = await supabase.rpc('list_branch_support_tickets', {
    p_branch_id: branchId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as SupportTicketRow[];
}

export async function countMyTickets(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('id', { count: 'exact', head: true })
    .eq('auth_user_id', userId);

  if (error) {
    throw error;
  }
  return count ?? 0;
}

export async function countBranchSupportTickets(branchId: string): Promise<number> {
  const { data, error } = await supabase.rpc('count_branch_support_tickets', {
    p_branch_id: branchId,
  });
  if (error) {
    throw error;
  }
  return (data as number) ?? 0;
}

export async function getSupportTicketById(ticketId: string): Promise<SupportTicketRow | null> {
  const { data, error } = await supabase.from('support_tickets').select(ticketSelect).eq('id', ticketId).maybeSingle();

  if (error) {
    throw error;
  }
  return (data as SupportTicketRow) ?? null;
}

const threadSelect = 'id, author, body, staff_label, created_at';

export async function listSupportTicketThreadMessages(ticketId: string): Promise<SupportThreadMessageRow[]> {
  const { data, error } = await supabase
    .from('support_ticket_thread_messages')
    .select(threadSelect)
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }
  return (data ?? []) as SupportThreadMessageRow[];
}

/** Máximo del cuerpo inicial al crear ticket desde Feedback. */
export const SUPPORT_TICKET_BODY_MAX = 8000;

const COMMERCE_FOLLOWUP_MAX = 20000;

/** Invalida el cache del badge de soporte (p. ej. tras marcar leído). */
export function invalidateSupportUnreadCache(): void {
  supportTicketsCache = null;
  supportTicketsInFlight = null;
}

export async function addSupportTicketCommerceMessage(ticketId: string, body: string): Promise<string> {
  const b = body.trim();
  if (b.length < 1 || b.length > COMMERCE_FOLLOWUP_MAX) {
    throw new Error('El mensaje no es válido.');
  }
  const { data, error } = await supabase.rpc('add_support_ticket_commerce_message', {
    p_ticket_id: ticketId,
    p_body: b,
  });
  if (error) {
    throw error;
  }
  if (typeof data !== 'string') {
    throw new Error('No se pudo registrar el seguimiento.');
  }
  return data;
}

export function hasUnreadSupportReply(ticket: SupportTicketRow): boolean {
  return Boolean(ticket.replied_at && !ticket.read_by_user_at);
}

/**
 * Respuestas publicadas aún no marcadas como leídas (badge / resumen de perfil).
 * `force` saltea cache de 1h (poll / focus / AppState).
 */
export async function countUnreadSupportReplies(opts?: { force?: boolean }): Promise<number> {
  if (!canRunOperationalBackgroundWork()) return 0;

  if (opts?.force) {
    invalidateSupportUnreadCache();
  }

  const now = Date.now();
  if (supportTicketsCache && now - supportTicketsCache.fetchedAt < SUPPORT_TICKETS_CACHE_MS) {
    return supportTicketsCache.value;
  }
  if (supportTicketsInFlight) {
    return supportTicketsInFlight;
  }

  supportTicketsInFlight = countUnreadSupportRepliesRemote().finally(() => {
    supportTicketsInFlight = null;
  });
  return supportTicketsInFlight;
}

async function countUnreadSupportRepliesRemote(): Promise<number> {
  const { data, error } = await supabase.rpc('count_my_unread_support_tickets');
  if (error) {
    throw error;
  }
  const value = typeof data === 'number' ? data : Number(data ?? 0);
  const normalized = Number.isFinite(value) ? value : 0;
  supportTicketsCache = { value: normalized, fetchedAt: Date.now() };
  return normalized;
}

export async function markSupportTicketRead(ticketId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_support_ticket_read', { p_ticket_id: ticketId });
  if (error) {
    throw error;
  }
  invalidateSupportUnreadCache();
}

const RESOLUTION_MAX_NOTE = 1000;

export async function submitSupportTicketUserResolution(
  ticketId: string,
  resolution: UserTicketResolution,
  note?: string
): Promise<void> {
  const n = note?.trim() ?? '';
  if (n.length > RESOLUTION_MAX_NOTE) {
    throw new Error(`El comentario no puede superar ${RESOLUTION_MAX_NOTE} caracteres.`);
  }
  const { error } = await supabase.rpc('set_support_ticket_user_resolution', {
    p_ticket_id: ticketId,
    p_resolution: resolution,
    p_note: n.length > 0 ? n : null,
  });
  if (error) {
    throw error;
  }
}
